import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { logError } from 'lib/logging'
import { useLocationEmailIdentity } from './useLocationEmailIdentity'

export const LocationEmailIdentityComponent = observer(function LocationEmailIdentityComponent(): ReactElement {
  const vm = useLocationEmailIdentity()

  if (vm.loading) return <p className="text-subtle">Loading…</p>

  return (
    <div className="flex max-w-xl flex-col gap-4">
      <div>
        <Label htmlFor="sending-domain">Sending domain</Label>
        <Select value={vm.selectedValue} onValueChange={vm.setSelectedValue}>
          <SelectTrigger id="sending-domain">
            <SelectValue placeholder="Choose a domain" />
          </SelectTrigger>
          <SelectContent>
            {vm.domainChoices.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="local-part">From address</Label>
        <div className="flex items-center gap-2">
          <Input
            id="local-part"
            placeholder="downtown"
            value={vm.localPart}
            onChange={(e) => vm.setLocalPart(e.target.value)}
            className="flex-1"
          />
          {vm.selectedDomainName && <span className="text-subtle">@{vm.selectedDomainName}</span>}
        </div>
        {vm.localPartError && <p className="mt-1 text-sm text-danger-default">{vm.localPartError}</p>}
        {!vm.localPartError && vm.availability === `checking` && (
          <p className="mt-1 text-sm text-subtle">Checking availability…</p>
        )}
        {!vm.localPartError && vm.availability === `taken` && (
          <p className="mt-1 text-sm text-danger-default">That address is already in use. Choose another.</p>
        )}
        {!vm.localPartError && vm.availability === `available` && (
          <p className="mt-1 text-sm text-success-default">Available.</p>
        )}
      </div>

      <div>
        <Label htmlFor="from-name">From Name</Label>
        <Input
          id="from-name"
          placeholder="Smith Dental - Downtown"
          value={vm.fromName}
          onChange={(e) => vm.setFromName(e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="reply-to">Reply-To</Label>
        <Input
          id="reply-to"
          placeholder="frontdesk@smithdental.com"
          value={vm.replyTo}
          onChange={(e) => vm.setReplyTo(e.target.value)}
        />
      </div>

      {vm.previewAddress && vm.availability === `available` && (
        <p className="text-sm text-subtle">
          Patients will see email from{` `}
          <span className="font-medium text-strong">{vm.fromName.trim() || vm.previewAddress}</span>.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button onClick={() => vm.onSave().catch(logError)} disabled={!vm.canSave}>
          {vm.saving ? `Saving…` : `Save`}
        </Button>
        {vm.identity && (
          <Button variant="tertiary" onClick={() => vm.onClear().catch(logError)}>
            Clear
          </Button>
        )}
      </div>
    </div>
  )
})
